import { useState, useEffect, useRef } from 'react';
import { FiMessageSquare, FiX } from 'react-icons/fi';
import './Chatbot.css';
import { BsThreeDots } from 'react-icons/bs';

const apiKey = process.env.REACT_APP_OPENROUTER_API_KEY;

//   - Chicken Burgers:
//     - Classic Chicken Burger: Fried chicken breast, pickles & creamy mayo in a brioche bun
//     - The Spicy Deluxe Burger: Fried chicken breast, cheddar cheese, lettuce, tomato, pickles & mayo in a brioche bun
//     - Korean Chicken Burger: Fried chicken breast, cheddar cheese, lettuce, tomato, pickles & mayo with Korean sauce in a brioche bun
//     - Nashville Hot Chicken Burger: Fried chicken breast, cheddar cheese, lettuce, tomato, pickles & mayo with Nashville sauce in a brioche bun
//   - Beef Burgers:
//     - House Cheese Smash Burger: Cheese, SMASH sauce, ketchup, onion & pickles
//     - Double Classic Smash: £10.95, Cheese, SMASH sauce, ketchup, lettuce, tomato, onion & pickles
//     - Ultimate Angus: Angus patty with cheddar cheese, lettuce, tomatoes, pickles, homemade burger sauce in a brioche bun

const menu = `
  Wok & Fire menu offers:
  - Chicken Wings & Tenders:
    - Sizes: 5, 10, or 16 Wings or Tenders
    - Glazes: Sweet Chilli, Peri Peri, Nashville, Korean
  - Corn Dogs:
    - Original Corn Dog: £4.50
    - Half & Half Corn Dog: £4.50
    - Potato Corn Dog: £4.95
    - Potato & Mozzarella Corn Dog: With any 3 sauces (Mayo, Ketchup, Mustard, Sweet Chilli, Wok & Chili)
  - Meal Deals:
    - Chicken Rice Bowl
    - Wings / Tender Meal: 6 Wings or Tenders, Fries & Drink
    - Corn Dog Meal: Any Corn Dog, Fries & Drink
    - Wok & Fire Meat Box: 3 Wings with loaded fries, mayo, sausage
    - Wok & Fire Meal Deal: Any base + 2 favourites + drink
  - WOK & FIRE Build Your Box:
    - Bases: Egg Noodles, Whole-White Noodles, Rice Noodles, Udon Noodles, Jasmine Rice, Whole-Grain Rice
    - Stir-Fry Veg: Broccoli, Carrot, Mushroom, Onion, Pak Choi, Leek, Cabbage, Soya Edamame Beans
    - Favourites: Chicken Breast, Chicken Tender, Chicken Katsu, Chicken Wings, Beef, Roast Duck, Mixed Seafood (+£2.95), Prawn, King Prawn, Tofu, Button Mushroom, Pak Choi, Cashew Nut, Bamboo Shoot, Mixed Peppers, Red Onion, Pineapple, Baby Corn, Broccoli, Soya Edamame Beans
    - Sauces: Teriyaki, Spicy Teriyaki, Sweet & Sour, Yellow Curry & Coconut, Dark Soya & Ginger, Asian Spice Sauce, Hot Asia, Garlic & Black Pepper, Peanut Sauce, Soya Sauce, Sweet Chili
    - Extras: Fresh Coriander, Fried Garlic, Fried Onion, Peanut, Sesame Seeds
  - Sides:
    - Regular / Large Fries
    - Dirty Fries
    - Loaded Fries 
  - Drinks:
    - Coca Cola, Coca Cola Zero, Sprite, Fanta, Fresh Orange / Apple Juice, Ice Tea (Lemon Sparkling, Green, Peach), Water, Sparkling Water
    - Milkshakes: Vanilla, Strawberry, Chocolate
  - Desserts:
    - Brownie
    - Sticky Toffee Pudding
`;

const systemPrompt = `
You are a friendly restaurant chatbot for Wok & Fire.
When replying:
- Use 2-3 short lines.
- Use emojis 🍔 🍜 🥤 when helpful.
- Format replies with clear line breaks.
- Use bullet points with emojis or symbols.
- Highlight item names by capitalizing or surrounding with *.
- Keep replies easy, fun, and readable.
Here is the menu for reference:
${menu}
`;

// Helper function: check if last greeting was sent more than 1 hour ago
const hasOneHourPassed = () => {
    const lastGreeting = localStorage.getItem('lastGreetingTimestamp');
    if (!lastGreeting) return true;
    const lastTime = parseInt(lastGreeting, 10);
    const now = Date.now();
    return now - lastTime > 60 * 60 * 1000; // 1 hour in ms
};

const Chatbot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState([
        {
            sender: 'bot',
            text: '👋 Hi there! I’m Wok & Fire’s assistant.\nAsk about Meal Deals, boxes 🍜, or drinks 🥤!',
            // text: '👋 Hi there! I’m Wok & Fire’s assistant.\nAsk about burgers 🍔, boxes 🍜, or drinks 🥤!',
            suggestions: ['Build Your Box', 'Meal Deals', 'Desserts'],
            // suggestions: ['Chicken Burgers', 'Build Your Box', 'Meal Deals', 'Desserts'],
        },
    ]);
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    const fetchBotReply = async (userInput) => {
        setLoading(true);
        try {
            const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${apiKey}`,
                },
                body: JSON.stringify({
                    model: 'mistralai/mistral-7b-instruct',
                    messages: [
                        { role: 'system', content: systemPrompt },
                        { role: 'user', content: userInput },
                    ],
                    temperature: 0.7,
                }),
            });

            const data = await response.json();
            setLoading(false);

            if (!response.ok || !data.choices?.[0]?.message?.content) {
                console.error('API error:', data);
                return 'Hmm 🤔 I didn’t catch that. Try asking about a burger or deal!';
            }

            return data.choices[0].message.content.trim();
        } catch (error) {
            setLoading(false);
            console.error('API error:', error);
            return '⚠️ Sorry, something went wrong. Try again in a bit!';
        }
    };

    const sendMessage = async (text) => {
        if (!text.trim()) return;

        setMessages((prev) => [...prev, { sender: 'user', text }]);

        // Check if greeting needed
        let botText = '';
        if (hasOneHourPassed()) {
            // First reply within 1 hour: add greeting prefix
            const greeting = `👋 Hello! Welcome back to Wok & Fire! Here's what I found for you:\n\n`;
            const reply = await fetchBotReply(text);
            botText = greeting + reply;
            localStorage.setItem('lastGreetingTimestamp', Date.now().toString());
        } else {
            // Just normal reply
            botText = await fetchBotReply(text);
        }

        setMessages((prev) => [...prev, { sender: 'bot', text: botText }]);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        sendMessage(input);
        setInput('');
    };

    return (
        <div className="chatbot-container">
            {!isOpen ? (
                <button
                    className="chatbot-icon"
                    onClick={() => setIsOpen(true)}
                    aria-label="Open chat"
                >
                    <FiMessageSquare size={24} />
                </button>
            ) :
                (
                    <div className="chatbot-box">
                        <div className="chatbot-header">
                            <h3 className='chatbot_logo_name'>Wok & Fire</h3>
                            <button
                                className="chatbot-close"
                                onClick={() => setIsOpen(false)}
                                aria-label="Close chat"
                            >
                                <FiX size={18} />
                            </button>
                        </div>

                        <div className="chatbot-messages">
                            {messages.map((msg, i) => (
                                <div key={i} className={`message ${msg.sender}`}>
                                    <p style={{ whiteSpace: 'pre-line' }}>{msg.text}</p>
                                    {msg.suggestions && (
                                        <div className="suggestions">
                                            {msg.suggestions.map((s) => (
                                                <button
                                                    key={s}
                                                    className="suggestion-btn"
                                                    onClick={() => sendMessage(s)}
                                                >
                                                    {s}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                            {loading && (
                                <div className="message bot">
                                    <p className="flex items-center gap-2 text-sm text-gray-500">
                                        <em>Wok & Fire is typing</em>
                                        <span className="relative flex items-center h-4 w-6">
                                            <span className="dot bg-gray-500" />
                                            <span className="dot bg-gray-500" />
                                            <span className="dot bg-gray-500" />
                                        </span>
                                    </p>


                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        <form className="chatbot-input" onSubmit={handleSubmit}>
                            <input
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Ask about burgers, boxes..."
                                aria-label="Chat input"
                                disabled={loading}
                            />
                            <button type="submit" disabled={loading}>
                                Send
                            </button>
                        </form>
                    </div>
                )}
        </div>
    );
};

export default Chatbot;
